/**
 * A simple wrapper around aptrinsic('track', eventName, eventDetails)
 * Please keep this function close to the vanilla!
 *
 * ref: https://support.gainsight.com/PX/API_for_Developers/02Usage_of_Different_APIs/Use_Custom_Event_API
 *
 * @param eventName name of the event
 * @param eventDetails details of the event.
 *      The value must of string, number or boolean. ISO 8601 date strings understood by gainsight.
 *      Using other data types may result in undefined bahaviour from gainsight.
 */

import BenchlingConfig from 'BenchlingConfig';
import {HOMEPAGE_ANALYTICS_ACTIONS} from 'constants/analytics-products/HomepageAnalyticsConstants';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {
  COMMON_ANALYTICS_ACTIONS,
  CommonAnalyticsActions,
  DEEP_RESEARCH_ANALYTICS_ACTIONS,
  ELN_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import * as logging from 'util/Logging';

const GAINSIGHT_EVENT_WHITELIST = new Set([
  COMMON_ANALYTICS_ACTIONS[CommonAnalyticsActions.SUBMITTED_BETA_REQUEST].action,
  COMMON_ANALYTICS_ACTIONS[CommonAnalyticsActions.SUBMITTED_CREATE_ANALYSIS].action,
  DEEP_RESEARCH_ANALYTICS_ACTIONS.SAVED_DEEP_RESEARCH_TO_ENTRY,
  ELN_ANALYTICS_ACTIONS.ACCEPTED_FLEXIBLE_DATA_ENTRY_RESULT,
  ELN_ANALYTICS_ACTIONS.OPEN_MODAL_FROM_FLEXIBLE_DATA_ENTRY_POPOVER,
  ELN_ANALYTICS_ACTIONS.OPENED_FLEXIBLE_DATA_ENTRY_MODAL,
  LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_SUGGESTED_ACTION.action,
  LLM_CHAT_ANALYTICS_ACTIONS.CREATED_LLM_CHAT_FROM_SUGGESTED_ACTION.action,
  LLM_CHAT_ANALYTICS_ACTIONS.OPENED_AI_SIDEPANEL.action,
  LLM_CHAT_ANALYTICS_ACTIONS.OPENED_AI_TENANT_SETTINGS.action,
  LLM_CHAT_ANALYTICS_ACTIONS.OPENED_LLM_CHAT_FROM_HISTORY.action,
  LLM_CHAT_ANALYTICS_ACTIONS.SENT_LLM_CHAT_MESSAGE.action,
  LLM_CHAT_ANALYTICS_ACTIONS.SUBMITTED_LLM_CHAT_REACTION_FEEDBACK.action,
  HOMEPAGE_ANALYTICS_ACTIONS.OPTED_OUT.action,
]);

function trackCustomEvent(
  eventName: string,
  eventDetails: Record<string, string | number | boolean | null | undefined>
): void {
  if (window.aptrinsic) {
    window.aptrinsic('track', eventName, eventDetails);
  } else if (BenchlingConfig.ENABLE_GAINSIGHT_PX) {
    // There is a chance that aptrinsic hasn't loaded yet, so capture it as a callback.
    // See: benchling/templates/snippets/gainsightpx.html
    window.bnchAptrinsicCallbacks ||= [];
    window.bnchAptrinsicCallbacks.push(() => {
      // bnchAptrinsicCallbacks will only be called after aptrinsic has loaded.
      window.aptrinsic!('track', eventName, eventDetails);
    });
  } else if (window.DEPLOY_ENVIRONMENT !== 'dev-manual') {
    logging.warn('Gainsight not enabled');
  }
}

const gainsight = {trackCustomEvent, GAINSIGHT_EVENT_WHITELIST};

export default gainsight;
